<template>
  <div class="">

    <div class="label-line flex flex-wrap">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputName
          label="Full name as registered with ASIC" 
          objectType="company"
          propertyKey="name"
          :value="applicant.company.name"
          :updateValue="updateValue"
          :onKeyup="updateCompanyName"
          :required="true"></InputName>
      </div>
    </div>

    <div class="mt-8 flex flex-wrap">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputABN label="ABN" 
          propertyKey="abn"
          :value="applicant.company.abn"
          objectType="company"
          :updateValue="updateValue"
          :required="true"></InputABN>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputText 
          label="Business activity"
          objectType="company" 
          propertyKey="activity"
          :value="applicant.company.activity"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <div class="label-standard">Registered as</div>

        <div class="mt-2">
          <select 
            :value="applicant.company.registered_as"
            v-on:change="updateValue({
              objectType: 'company',
              key       : 'registered_as', 
              value     : $event.target.value
            })"
            class="select-standard">
            <option value="-1" disabled="">Select an option...</option>
            <option value="0">Proprietary</option>
            <option value="1">Public</option>
            <option value="2">Other</option>
          </select>
        </div>        
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <div class="label-standard">Role</div>

        <div class="mt-2">
          <select 
            :value="applicant.company.role == null ? -1 : applicant.company.role"
            v-on:change="updateValue({
              objectType: 'company',
              key       : 'role', 
              value     : $event.target.value
            })"
            class="select-standard">
            <option value="-1" disabled="">Select an option...</option>
            <option value="0">Borrower</option>
            <option value="1">Guarantor</option>
            <option value="2">Trustee</option>
          </select>
        </div>        
      </div>

      <div class="w-full lg:w-1/3 mt-8 lg:mt-0">
        <InputDatePicker 
          :maxDate="today"
          label="Date of incorporation"
          objectType="company"
          propertyKey="incorporated_at"
          :value="applicant.company.incorporated_at"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputPhone 
          label="Phone" 
          objectType="company"
          propertyKey="phone"
          :value="applicant.company.phone"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputPhone 
          label="Fax" 
          objectType="company"
          propertyKey="faxPhone"
          :value="applicant.company.faxPhone"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 mt-8 lg:mt-0">
        <InputEmail 
          label="Email address" 
          propertyKey="email"
          objectType="company"
          :value="applicant.company.email"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 flex flex-wrap">

        <div class="w-1/3 pr-2">
          <BuildingTypeSelector
            propertyKey="building_type"
            objectType="registered_address"
            :objectId="applicant.company.registered_address.id"
            :value="applicant.company.registered_address.building_type"
            :updateValue="updateValue" />
        </div>

        <div class="w-1/3 pr-2">
          <InputText 
            label="Unit number"
            propertyKey="unit_number"
            objectType="registered_address"
            :objectId="applicant.company.registered_address.id"
            :value="applicant.company.registered_address.unit_number"
            :updateValue="updateValue" />
        </div>

        <div class="w-1/3">
          <InputText 
            label="Street number"
            propertyKey="street_number"
            objectType="registered_address"
            :objectId="applicant.company.registered_address.id"
            :value="applicant.company.registered_address.street_number"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0 flex flex-wrap">
        <div class="w-3/4 pr-2">
          <InputText 
            label="Street name"
            propertyKey="street_name"
            objectType="registered_address"
            :objectId="applicant.company.registered_address.id"
            :value="applicant.company.registered_address.street_name"
            :updateValue="updateValue" />

          <div class="mt-1 text-right" v-if="applicant.company.registered_address.street_name != null && applicant.company.registered_address.street_name.length > 0">
            <a href="#" class="text-gray-700 hover:underline" @click.prevent="$modal.show('map-modal', { 
              address : getAddressText() 
            })">Show this address on map</a>
          </div>
        </div>

        <div class="w-1/4 pl-1">
          <StreetTypeSelector
            propertyKey="street_type"
            objectType="registered_address"
            :objectId="applicant.company.registered_address.id"
            :value="applicant.company.registered_address.street_type"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <InputText 
          label="Suburb" 
          objectType="registered_address"
          :objectId="applicant.company.registered_address.id"
          propertyKey="suburb"
          :value="applicant.company.registered_address.suburb"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="flex flex-wrap label-line">
      <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
        <InputPostcode 
          label="Postcode" 
          objectType="registered_address"
          :objectId="applicant.company.registered_address.id"
          propertyKey="postcode"
          :value="applicant.company.registered_address.postcode"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 mt-8 lg:mt-0 pr-0 lg:pr-3">
        <StateSelector
          objectType="registered_address"
          :objectId="applicant.company.registered_address.id"
          propertyKey="state_id"
          :value="applicant.company.registered_address.state_id"
          :updateValue="updateValue" />
      </div>

      <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
        <CountrySelector
          objectType="registered_address"
          :objectId="applicant.company.registered_address.id"
          propertyKey="country_id"
          :value="applicant.company.registered_address.country_id"
          :updateValue="updateValue" />
      </div>
    </div>

    <div class="mt-8">
      <PrincipalPlace :companyId="applicant.company.id"></PrincipalPlace>
    </div>

    <!-- load list of company directors for this company -->
    <div class="mt-8">
      <Directors :companyId="applicant.company.id"></Directors>
    </div>
  </div>
</template>

<script>
  import TitleSelector        from '@/shared/components/form/TitleSelector'
  import StateSelector        from '@/shared/components/form/StateSelector'
  import StreetTypeSelector   from '@/shared/components/form/StreetTypeSelector'
  import BuildingTypeSelector from '@/shared/components/form/BuildingTypeSelector'
  import CountrySelector      from '@/shared/components/form/CountrySelector'
  import InputText            from '@/shared/components/form/InputText'
  import InputName            from '@/shared/components/form/InputName'
  import InputPhone           from '@/shared/components/form/InputPhone'
  import InputPostcode        from '@/shared/components/form/InputPostcode'
  import InputABN             from '@/shared/components/form/InputABN'
  import InputEmail           from '@/shared/components/form/InputEmail'
  import InputDatePicker      from '@/shared/components/form/InputDatePicker'
  import Directors            from './Directors'
  import PrincipalPlace       from './PrincipalPlace'
  import { EventBus }         from '@/shared/event-bus';

  export default {

    props : {
      applicant : {
        type     : Object,
        required : true
      }
    },

    components: {
      TitleSelector,
      StateSelector,
      InputText,
      InputName,
      InputABN,
      InputPhone,
      InputEmail,
      InputPostcode,
      InputDatePicker,
      CountrySelector,
      PrincipalPlace,
      Directors,
      StreetTypeSelector,
      BuildingTypeSelector
    },

    created() {
      this.logActivity({
        type        : 'company-page-visited',
        clientId    : this.$route.params.clientId,
        applicantId : this.$route.params.applicantId,
        currentView : this.$store.state.view
      })
    },

    methods : {

      updateCompanyName(value) {
        this.$store.commit(this.$store.state.view + "Module/setApplicantName", {
          name       : value,
          applicantId: this.applicant.id
        })
      },

      // a method that will receive the value and update
      updateValue(params) {

        if(params.objectType == 'company') {
          this.updateCompany(this.applicant.company, params.key, params.value)
        }

        else if(params.objectType == 'registered_address') {
          this.updateAddress(this.applicant.company[params.objectType], params.key, params.value)

          // fire off the registered address changed event
          // so that we can update principal address if they have to be the same
          EventBus.$emit('company-registered-address-changed', this.applicant.company.registered_address)
        }
      },
    }
  }
</script>